<script setup lang="ts">
import { useActionBarStore } from '@/store/actionBar'
import { useBuilderStore } from '@/store/builder'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  UIDrawer,
  UIDrawerContent,
  UISelect,
  UIFormItem,
  UIInput,
  UITooltip,
  UIDivider,
  UIInputGroup,
  UIRadioGroup,
  UIRadio,
  UIButton,
  UIPopover,
  UIContentWrap,
} from '@gohighlevel/ghl-ui'
import {
  InfoCircleIcon,
  Trash01Icon,
  Upload01Icon,
} from '@gohighlevel/ghl-icons/24/outline'

import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useEmailTemplates } from '@/composition'

import {
  InputWithCustomValues,
  validateEmail,
  Switch,
  redirectTypes,
  isValidUrl,
  AttachmentUploadModal,
  bytes,
  useCustomNotificationSettings,
} from '@gohighlevel/ghl-proposals-common'
import { useAppStore } from '@/store/app'
import { isEmpty, cloneDeep, isNil } from 'lodash'
import { IProposalCustomerCustomNotificationSettings } from '@gohighlevel/ghl-proposals-common/src/services/CustomNotificationService'
import {
  useAttachments,
  ALLOWED_FILE_TYPES,
  getFileTypeIcon,
} from '@/composition/useAttachments'

const store = useBuilderStore()
const appStore = useAppStore()
const route = useRoute()
const i18nLocale = useI18n()
const { t } = i18nLocale
const storeAction = useActionBarStore()
const { emailTemplateList, fetchEmailTemplates } = useEmailTemplates()
const { notificationSetting } = useCustomNotificationSettings(
  route.params.locationId as string
)
interface NotificationSettings {
  _id?: string
  [key: string]: any
}

const validEmailAttachments = computed(() => {
  return appStore.document.emailAttachments || []
})
const previousSettings = ref<NotificationSettings>({})
const isSubjectEdited = ref(false)
const notificationSettings = reactive({
  overrideDocumentNotificationSettings:
    appStore.document?.notificationSettings?.sender &&
    (!isNil(appStore.document?.notificationSettings?.sender?.fromEmail) ||
      !isNil(appStore.document?.notificationSettings?.sender?.fromName))
      ? true
      : false,
  enableDocumentReceiveNotification: appStore.document.notificationSettings
    ?.receive
    ? true
    : notificationSetting.value.enableDocumentReceiveNotification,
  overrideReceivedEmailSender:
    appStore.document.notificationSettings?.sender?.fromEmail ||
    notificationSetting.value.overrideReceivedEmailSender,
})

const templates = reactive({
  documentReceivedTemplateId:
    appStore.document.notificationSettings?.receive?.templateId ||
    notificationSetting.value.documentReceivedTemplateId,
  documentReceivedSubject:
    appStore.document.notificationSettings?.receive?.subject ||
    notificationSetting.value.documentReceivedSubject,
  documentReceivedEmail:
    appStore.document.notificationSettings?.sender?.fromEmail ||
    notificationSetting.value.documentReceivedEmail,
  documentReceivedEmailFromName:
    appStore.document.notificationSettings?.sender?.fromName ||
    notificationSetting.value.documentReceivedEmailFromName,
})

const redirectSettings = reactive({
  enableDocumentRedirectUrl:
    appStore.document.redirectSettings?.enableDocumentRedirectUrl || false,
  documentRedirectUrl: appStore.document.redirectSettings?.documentRedirectUrl,
  documentRedirectType:
    appStore.document.redirectSettings?.documentRedirectType,
})

const showAttachmentModal = ref(false)
const { removeAttachment } = useAttachments()

onMounted(() => {
  fetchEmailTemplates()
})

watch(
  () => appStore.document.notificationSettings,
  newSettings => {
    delete newSettings?.sender?.value
    if (!isNil(newSettings?.receive?.templateId)) {
      templates.documentReceivedTemplateId = newSettings.receive.templateId
    } else {
      templates.documentReceivedTemplateId =
        notificationSetting.value.documentReceivedTemplateId
    }

    if (!isNil(newSettings?.receive?.subject)) {
      templates.documentReceivedSubject = newSettings.receive.subject
    } else {
      templates.documentReceivedSubject =
        notificationSetting.value.documentReceivedSubject
    }

    if (!isNil(newSettings?.sender?.fromName)) {
      templates.documentReceivedEmailFromName = newSettings.sender.fromName
    } else {
      templates.documentReceivedEmailFromName =
        notificationSetting.value.documentReceivedEmailFromName
    }

    if (!isNil(newSettings?.sender?.fromEmail)) {
      templates.documentReceivedEmail = newSettings.sender.fromEmail
    } else {
      templates.documentReceivedEmail =
        notificationSetting.value.documentReceivedEmail
    }
  },
  { deep: true }
)

const validateField = (field: string, value: string) => {
  const specialCharRegex = /[^a-zA-Z0-9\s]/

  if (
    !notificationSettings.overrideDocumentNotificationSettings ||
    !appStore.sendAttempted
  ) {
    return ''
  }

  switch (field) {
    case 'documentReceivedEmailFromName':
      if (!value) {
        return t('validations.required.senderName')
      }
      if (specialCharRegex.test(value)) {
        return t('validations.invalid.nameFormat')
      }
      break
    case 'documentReceivedEmail':
      if (!value) {
        return t('validations.required.email')
      } else if (!validateEmail(value)) {
        return t('validations.invalid.emailFormat')
      }
      break
    case 'documentReceivedSubject':
      if (!value && isSubjectEdited.value) {
        return t('validations.required.subjectField')
      }
      break
  }
  return ''
}

const onUpdate = (val: string, key: keyof typeof templates) => {
  templates[key] = val
  if (key === 'documentReceivedSubject') {
    isSubjectEdited.value = true
    appStore.updateDocumentNotificationReceiveSettings({
      receive: {
        subject: val,
      },
    })
  } else if (key === 'documentReceivedTemplateId') {
    appStore.updateDocumentNotificationReceiveSettings({
      receive: {
        templateId: val,
      },
    })
  } else if (key === 'documentReceivedEmail') {
    appStore.updateDocumentNotificationSenderSettings({
      sender: {
        fromEmail: val,
      },
    })
  } else if (key === 'documentReceivedEmailFromName') {
    appStore.updateDocumentNotificationSenderSettings({
      sender: {
        fromName: val,
      },
    })
  }
}

const DefaultSenderNotificationSettings = () => {
  const payload = {
    sender: {
      fromEmail: templates.documentReceivedEmail || '',
      fromName: templates.documentReceivedEmailFromName || '',
    },
  }
  return payload
}

const onToggleSwitch = (value: boolean) => {
  notificationSettings.overrideDocumentNotificationSettings = value

  // In case of disabling the switch, store the current settings and clear them
  if (!value) {
    previousSettings.value = cloneDeep({
      sender: {
        fromName: templates.documentReceivedEmailFromName,
        fromEmail: templates.documentReceivedEmail,
      },
    })
    appStore.updateDocumentNotificationSenderSettings({ sender: {} })
  } else {
    // Restore the previous settings if available else set global settings as the settings for the document
    if (!isEmpty(previousSettings.value)) {
      appStore.updateDocumentNotificationSenderSettings(previousSettings.value)
      previousSettings.value = {}
    } else {
      appStore.updateDocumentNotificationSenderSettings(
        DefaultSenderNotificationSettings()
      )
    }

    appStore.setUnsavedChanges(true)
  }
}

const onUpdateRedirectSettings = (
  val: string,
  key: keyof Pick<
    IProposalCustomerCustomNotificationSettings,
    'documentRedirectUrl' | 'documentRedirectType'
  >
) => {
  redirectSettings[key] = val
  appStore.setRedirectSettings(redirectSettings)
}

const handleRedirectTypeChange = (event: any) => {
  onUpdateRedirectSettings(event.target.value, 'documentRedirectType')
}

const handleEnableRedirectUrl = () => {
  appStore.setRedirectSettings(redirectSettings)
}

const onClose = () => {
  storeAction.toggleDocumentSettings()
}

const validateRedirectUrl = () => {
  if (!appStore.sendAttempted) {
    return ''
  }

  if (
    redirectSettings.enableDocumentRedirectUrl &&
    !redirectSettings.documentRedirectUrl
  ) {
    return t('validations.required.redirectUrl')
  }

  const url = redirectSettings.documentRedirectUrl || ''
  const replacedUrl = /^https?:\/\//i.test(url) ? `${url}` : `https://${url}`
  if (
    redirectSettings.enableDocumentRedirectUrl &&
    redirectSettings?.documentRedirectUrl &&
    !isValidUrl(replacedUrl)
  ) {
    return t('validations.invalid.urlFormat')
  }
  return ''
}
</script>

<template>
  <UIDrawer
    :width="380"
    :trap-focus="false"
    id="document-settings-drawer"
    :show="storeAction.documentSettings"
    @update:show="onClose"
    :placement="'left'"
    to="#drawer-target"
    class="!shadow-page h-screen"
  >
    <UIDrawerContent
      id="document-settings-drawer-content"
      :title="$t('common.documentSettings')"
    >
      <div class="relative">
        <div class="relative">
          <div class="relative flex items-center">
            <Switch
              :value="notificationSettings.overrideDocumentNotificationSettings"
              :label="$t('common.overrideEmailConfiguration')"
              :info-label="``"
              @update:value="onToggleSwitch"
              :disabled="!store.isDocumentEditable"
              class="mb-8 inline-flex"
            />
            <UITooltip
              :content="$t('common.overrideEmailConfigurationDescription')"
              :placement="'top'"
            >
              <template #trigger>
                <InfoCircleIcon class="w-4 h-4 text-gray-500 ml-1 mb-8" />
              </template>
              {{ $t('common.overrideEmailConfigurationDescription') }}
            </UITooltip>
          </div>
        </div>
        <div class="w-full">
          <UIContentWrap
            :locale="i18nLocale?.locale?.value || 'en-US'"
            class="w-full p-0"
            style="display: contents"
          >
            <div class="w-full flex justify-start">
              <UIFormItem
                :label="$t('common.fromName')"
                :show-feedback="false"
                class="w-full"
              >
                <UIInput
                  id="document_received_from_name"
                  v-model:value="templates.documentReceivedEmailFromName"
                  @update:value="
                    value => onUpdate(value, 'documentReceivedEmailFromName')
                  "
                  :disabled="
                    !store.isDocumentEditable ||
                    (store.isDocumentEditable &&
                      !notificationSettings.overrideDocumentNotificationSettings)
                  "
                  class="ml-auto"
                />
              </UIFormItem>
            </div>
          </UIContentWrap>
          <span
            v-if="
              validateField(
                'documentReceivedEmailFromName',
                templates.documentReceivedEmailFromName
              )
            "
            class="text-sm text-red-500 block w-full ml-auto"
            >{{
              validateField(
                'documentReceivedEmailFromName',
                templates.documentReceivedEmailFromName
              )
            }}</span
          >
          <UIContentWrap
            :locale="i18nLocale?.locale?.value || 'en-US'"
            class="w-full p-0"
            style="display: contents"
          >
            <div class="w-full mt-4 flex justify-start">
              <UIFormItem
                :label="$t('common.fromEmail')"
                :show-feedback="false"
                class="w-full"
              >
                <UIInput
                  id="document_received_from_email"
                  v-model:value="templates.documentReceivedEmail"
                  @update:value="
                    value => onUpdate(value, 'documentReceivedEmail')
                  "
                  :disabled="
                    !store.isDocumentEditable ||
                    (store.isDocumentEditable &&
                      !notificationSettings.overrideDocumentNotificationSettings)
                  "
                  class="ml-auto"
                />
              </UIFormItem>
            </div>
          </UIContentWrap>
          <span
            v-if="
              validateField(
                'documentReceivedEmail',
                templates.documentReceivedEmail
              )
            "
            class="text-sm text-red-500 block w-full ml-auto"
            >{{
              validateField(
                'documentReceivedEmail',
                templates.documentReceivedEmail
              )
            }}</span
          >
          <UIDivider />
          <div class="w-full mt-4">
            <InputWithCustomValues
              id="document_received"
              :label="$t('common.emailSubject')"
              v-model:value="templates.documentReceivedSubject"
              @update:value="
                value => onUpdate(value, 'documentReceivedSubject')
              "
              :disabled="!store.isDocumentEditable"
              :show-feedback="false"
            />
          </div>
          <span
            v-if="
              validateField(
                'documentReceivedSubject',
                templates.documentReceivedSubject
              )
            "
            class="text-sm text-red-500 block"
            >{{
              validateField(
                'documentReceivedSubject',
                templates.documentReceivedSubject
              )
            }}</span
          >
          <div class="w-full mt-4">
            <UIFormItem
              :label="$t('proposalsList.emailTemplate')"
              :show-feedback="false"
            >
              <UISelect
                id="email-template-for-document-rec"
                v-model:value="templates.documentReceivedTemplateId"
                :options="emailTemplateList"
                :placeholder="$t('proposalsList.selectTemplate')"
                @update:value="
                  value => onUpdate(value, 'documentReceivedTemplateId')
                "
                :disabled="!store.isDocumentEditable"
              />
            </UIFormItem>
          </div>
        </div>

        <UIDivider class="mt-2" />

        <Switch
          v-model:value="redirectSettings.enableDocumentRedirectUrl"
          :label="$t('common.enableRedirectionTitle')"
          :info-label="``"
          :disabled="!store.isDocumentEditable"
          @update:value="handleEnableRedirectUrl"
          class="mb-8"
        />

        <div class="bg-gray-100 p-2 rounded-sm">
          <div class="w-full mb-1">
            <UIContentWrap
              :locale="i18nLocale?.locale?.value || 'en-US'"
              class="p-0"
              style="display: contents"
            >
              <UIFormItem
                :label="$t('common.enterCustomUrl')"
                class="mt-2 text-sm text-gray-700"
                :style="{
                  '--n-label-text-color':
                    !store.isDocumentEditable ||
                    !redirectSettings.enableDocumentRedirectUrl
                      ? 'rgba(194, 194, 194, 1)'
                      : 'inherit',
                }"
                :show-feedback="validateRedirectUrl() ? false : true"
              >
                <UIInputGroup>
                  <UIInput
                    v-model:value="redirectSettings.documentRedirectUrl"
                    @update:value="
                      value =>
                        onUpdateRedirectSettings(value, 'documentRedirectUrl')
                    "
                    id="document-settings-redirectUrl"
                    :placeholder="$t('common.redirectUrlPlaceholder')"
                    type="text"
                    :disabled="
                      !store.isDocumentEditable ||
                      !redirectSettings.enableDocumentRedirectUrl
                    "
                  />
                </UIInputGroup>
              </UIFormItem>
            </UIContentWrap>
            <span
              v-if="validateRedirectUrl()"
              class="text-sm text-red-500 block"
              >{{ validateRedirectUrl() }}</span
            >
          </div>

          <div class="w-full pb-3">
            <UIRadioGroup
              :name="'radioGroupRedirectUrlDocumentSettings'"
              :disabled="
                !store.isDocumentEditable ||
                !redirectSettings.enableDocumentRedirectUrl
              "
              class="flex justify-between"
              :value="redirectSettings.documentRedirectType"
            >
              <UIRadio
                class="redirect-type-radio"
                :id="`redirect-type-${redirectType.value}`"
                v-for="redirectType in redirectTypes"
                :key="redirectType.value"
                :value="redirectType.value"
                :disabled="!store.isDocumentEditable"
                :label="redirectType.label"
                @change="handleRedirectTypeChange"
              />
            </UIRadioGroup>
          </div>
        </div>

        <UIDivider class="mt-2" />

        <div class="relative mb-8 w-full">
          <div class="flex items-center mb-4">
            <span class="text-sm font-medium">{{
              $t('common.addAttachments')
            }}</span>
            <div class="ml-2 flex items-center">
              <UITooltip
                :content="$t('common.attachmentsInfo')"
                :placement="'top'"
              >
                <template #trigger>
                  <InfoCircleIcon class="w-5 h-5 text-current" />
                </template>
                {{ $t('common.attachmentsInfo') }}
              </UITooltip>
            </div>
          </div>
          <div class="w-full">
            <UIButton
              id="upload-attachment-button"
              type="default"
              class="w-full flex items-center justify-center"
              :disabled="!store.isDocumentEditable"
              @click="store.isDocumentEditable && (showAttachmentModal = true)"
            >
              <Upload01Icon class="w-5 h-5 mr-1 text-current" />
              <span>{{ $t('common.upload') }}</span>
            </UIButton>
          </div>

          <!-- List of attachments -->
          <div
            v-if="validEmailAttachments?.length"
            class="mt-4 border border-gray-100 rounded-lg"
          >
            <div
              v-for="attachment in validEmailAttachments"
              :key="attachment.storagePath"
              class="flex items-center justify-between p-3 mb-2 last:mb-0"
            >
              <div class="flex items-center flex-1 min-w-0">
                <div
                  class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                >
                  <component
                    :is="getFileTypeIcon(attachment.type)"
                    class="w-5 h-5 text-gray-500"
                  />
                </div>
                <div class="ml-3 flex-1 min-w-0">
                  <UIPopover
                    trigger="hover"
                    content-style="background-color: rgb(3 7 18)"
                    arrow-style="background-color: rgb(3 7 18)"
                    style="padding: 0"
                  >
                    <template #trigger>
                      <div class="truncate text-sm font-medium">
                        {{ attachment.name }}
                      </div>
                    </template>
                    <div class="font-light text-neutral-50 p-2">
                      {{ attachment.name }}
                    </div>
                  </UIPopover>
                  <div class="text-xs text-gray-500">
                    {{ bytes.format(attachment.size || 0) }}
                  </div>
                </div>
              </div>
              <UIButton
                :id="`remove-attachment-${attachment.storagePath}`"
                type="default"
                size="medium"
                :quaternary="true"
                :circle="true"
                @click="removeAttachment(attachment.storagePath)"
                class="flex-shrink-0 ml-2"
                :disabled="!store.isDocumentEditable"
                :class="{
                  'opacity-50 cursor-not-allowed': !store.isDocumentEditable,
                }"
              >
                <Trash01Icon class="w-4 h-4 text-current font-medium" />
              </UIButton>
            </div>
          </div>
        </div>

        <!-- Bottom spacing -->
        <div class="h-20" />
      </div>
    </UIDrawerContent>
  </UIDrawer>

  <AttachmentUploadModal
    :show="showAttachmentModal"
    :document-id="route.params.documentId as string"
    :for-template="false"
    :use-attachments="useAttachments"
    :allowed-file-types="ALLOWED_FILE_TYPES"
    :current-attachments="validEmailAttachments"
    @close-modal="showAttachmentModal = false"
  />
</template>

<style lang="scss" scoped>
.text-red-500 {
  display: block;
}

.redirect-type-radio {
  --n-font-size: 14px !important;
}
</style>
